'use client';
import { BASE_URL, USE_SOCKETVIEW } from '@/common/constants';
import { getWebsiteCodeHeader } from '@/common/pageConfig';
import { useRecaptcha } from '@/common/useRecaptcha';
import React, { useEffect, useState } from 'react';
import { io } from 'socket.io-client';

const formatNumber = (num) => {
  return (num ?? 0).toLocaleString('vi-VN');
};

function getDeviceId() {
  const key = 'deviceId';
  const random9DigitNumber = Math.floor(Math.random() * 900000000).toString();
  let deviceId = localStorage.getItem(key);
  if (!deviceId) {
    deviceId = random9DigitNumber;
    localStorage.setItem(key, deviceId);
  }
  return deviceId;
}

async function getViews(code) {
  try {
    const res = await fetch(`${BASE_URL}/staticalViewDays/getTodayView`, {
      headers: {
        websiteCode: getWebsiteCodeHeader(code),
        'Content-Type': 'application/json',
      },
    });
    return await res.json();
  } catch {
    return {};
  }
}

function isMobile() {
  return /Android|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
}

const STATS = [
  {
    key: 'online',
    icon: (
      <svg
        xmlns="http://www.w3.org/2000/svg"
        width="36"
        height="36"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
        strokeLinejoin="round">
        <circle cx="9" cy="7" r="4" />
        <path d="M3 21v-2a4 4 0 0 1 4-4h4a4 4 0 0 1 4 4v2" />
        <path d="M16 3.13a4 4 0 0 1 0 7.75" />
        <path d="M21 21v-2a4 4 0 0 0-3-3.87" />
      </svg>
    ),
    labelKey: 'home.onlineNow',
    fallback: 'Đang truy cập',
    dataKey: 'lastConnectView',
  },
  {
    key: 'today',
    icon: (
      <svg
        xmlns="http://www.w3.org/2000/svg"
        width="36"
        height="36"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
        strokeLinejoin="round">
        <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
        <line x1="16" y1="2" x2="16" y2="6" />
        <line x1="8" y1="2" x2="8" y2="6" />
        <line x1="3" y1="10" x2="21" y2="10" />
        <line x1="12" y1="14" x2="12" y2="18" />
        <line x1="10" y1="16" x2="14" y2="16" />
      </svg>
    ),
    labelKey: 'home.todayViews',
    fallback: 'Lượt truy cập hôm nay',
    dataKey: 'totalView',
  },
  {
    key: 'month',
    icon: (
      <svg
        xmlns="http://www.w3.org/2000/svg"
        width="36"
        height="36"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
        strokeLinejoin="round">
        <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
        <line x1="16" y1="2" x2="16" y2="6" />
        <line x1="8" y1="2" x2="8" y2="6" />
        <line x1="3" y1="10" x2="21" y2="10" />
        <path d="M8 14h.01M12 14h.01M16 14h.01M8 18h.01M12 18h.01M16 18h.01" />
      </svg>
    ),
    labelKey: 'home.monthViews',
    fallback: 'Lượt truy cập tháng',
    dataKey: 'monthView',
  },
  {
    key: 'year',
    icon: (
      <svg
        xmlns="http://www.w3.org/2000/svg"
        width="36"
        height="36"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
        strokeLinejoin="round">
        <line x1="18" y1="20" x2="18" y2="10" />
        <line x1="12" y1="20" x2="12" y2="4" />
        <line x1="6" y1="20" x2="6" y2="14" />
        <line x1="2" y1="20" x2="22" y2="20" />
      </svg>
    ),
    labelKey: 'home.yearViews',
    fallback: 'Lượt truy cập năm',
    dataKey: 'yearView',
  },
];

const AccessInformation = ({ websiteCode }) => {
  let socket;
  let hasSentUpView = false;
  let isSocketError = false;
  let isSocketConnected = false;

  const [dataViews, setDataViews] = useState({});
  const { execute } = useRecaptcha();
  // const t = useTranslations('');

  const fetchViews = async (code) => {
    let res = await getViews(code);
    if (typeof res.socketIdWithUser !== 'undefined') {
      const socketIdWithUser = res.socketIdWithUser;
      if (socketIdWithUser.findIndex((item) => item.id === getDeviceId()) === -1) {
        res.totalView = (res?.totalView ?? 0) + 1;
        socketIdWithUser.push({ id: getDeviceId(), date: new Date().toISOString() });
      }
      res = {
        ...res,
        socketIdWithUser,
        lastConnectView: socketIdWithUser.length,
        monthView: res?.monthView || 1420,
        yearView: res?.yearView || 15800,
      };
    }
    setDataViews(res);
  };

  useEffect(() => {
    fetchViews(websiteCode);
  }, [websiteCode]);

  const handleSubmitUpView = async (isFirst) => {
    if (hasSentUpView) return;
    hasSentUpView = true;
    setTimeout(async () => {
      try {
        const recaptchaToken = await execute('submit');
        await fetch(`${BASE_URL}/staticalViewDays/connectToWeb`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ idSocket: getDeviceId(), isFirst, recaptchaToken }),
        });
      } catch {}
    }, 5000);
  };

  const handleSubmitDownView = async () => {
    try {
      const recaptchaToken = await execute('submit');
      await fetch(`${BASE_URL}/staticalViewDays/disconnectToWeb?idSocket=${getDeviceId()}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ recaptchaToken }),
      });
    } catch {}
  };

  useEffect(() => {
    if (USE_SOCKETVIEW === '1') {
      socket = io(BASE_URL.replace('/v1', ''));
      try {
        socket.on('connect_error', () => {
          if (!isSocketError) {
            isSocketError = true;
            handleSubmitUpView(true);
          }
        });
        socket.on('connect', () => {
          if (!isSocketConnected) {
            isSocketConnected = true;
            socket.emit('f', { function: 'view', deviceId: getDeviceId(), isFirst: true });
          }
        });
        socket.on('dataView', (data) => setDataViews(data));
      } catch {
        handleSubmitUpView(true);
      }
    } else {
      handleSubmitUpView(true);
    }

    setTimeout(() => {
      if (!isSocketConnected) {
        setInterval(() => fetchViews(), 1000 * 30);
      }
    }, 10 * 1000);

    const handleBeforeUnload = async () => {
      try {
        if (isSocketConnected) {
          socket.emit('f', { function: 'dis-view', deviceId: getDeviceId() });
        } else {
          handleSubmitDownView();
        }
      } catch {}
    };

    const handleFocus = () => {
      if (isSocketConnected) {
        socket.emit('f', { function: 'view', deviceId: getDeviceId() });
      } else {
        handleSubmitUpView();
      }
    };

    if (isMobile()) {
      window.addEventListener('focus', handleFocus);
      window.addEventListener('blur', handleBeforeUnload);
    } else {
      window.addEventListener('beforeunload', handleBeforeUnload);
    }

    return () => {
      if (socket) socket.disconnect();
      if (isMobile()) {
        window.removeEventListener('focus', handleFocus);
        document.removeEventListener('blur', handleBeforeUnload);
      } else {
        window.removeEventListener('beforeunload', handleBeforeUnload);
      }
    };
  }, []);

  return (
    <div className="w-full order-3 relative">
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 sm:gap-2.5">
        {STATS.map((stat) => (
          <div
            key={stat.key}
            className="flex items-center gap-2 sm:gap-3.5 rounded-lg sm:rounded-xl px-3 py-2.5 sm:px-4 sm:py-3.5 min-w-0"
            style={{ background: '#b71c1c', color: '#fff' }}>
            <span className="opacity-95 shrink-0 [&_svg]:w-6 [&_svg]:h-6 sm:[&_svg]:w-9 sm:[&_svg]:h-9">{stat.icon}</span>
            <div className="flex flex-col gap-0.5 min-w-0">
              <span className="text-base sm:text-[22px] font-bold text-white tracking-wide whitespace-nowrap truncate">
                {formatNumber(dataViews?.[stat.dataKey])}
              </span>
              <span className="text-[10px] sm:text-xs text-white/80 whitespace-nowrap truncate">{stat.fallback}</span>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default AccessInformation;
